import React, { AnchorHTMLAttributes, HTMLAttributes, ReactElement, ReactNode, useMemo, useRef } from "react";
import { cubicBezier, motion, MotionProps, useScroll, useTransform, circOut } from "framer-motion";
import { twMerge } from "tailwind-merge";
import {
    useMotionTemplate,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { useMediaQuery } from "../../utils/media_query";


type CustomAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & MotionProps;
type CustomDivProps = HTMLAttributes<HTMLDivElement> & MotionProps;
/**
 * Custom props for the `StaggeringTextAnimation` component.
 * 
 * @property {string} [className] - Additional classes to style the component.
 * @property {React.ReactNode | string} children - Content to be rendered inside the component.
 * @property {boolean} [inverse=false] - If `true`, applies the "inverse" style. Default is `false`.
 */
interface StaggeringTextAnimationProps extends CustomAnchorProps {
    inverse?: boolean,
    mirrorElement?: ReactElement | string,
    asChild?: boolean
}

const ROTATION_RANGE = 32.5;
const HALF_ROTATION_RANGE = ROTATION_RANGE / 2;
const IMG_PADDING = 12;


export const TiltCard: React.FC<CustomAnchorProps> = ({ className, children, ...props }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const xSpring = useSpring(x);
    const ySpring = useSpring(y);
    const transform = useMotionTemplate`rotateX(${xSpring}deg) rotateY(${ySpring}deg)`;
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!ref.current) return [0, 0];
        const rect = ref.current.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const mouseX = (e.clientX - rect.left) * ROTATION_RANGE;
        const mouseY = (e.clientY - rect.top) * ROTATION_RANGE;
        const rX = (mouseY / height - HALF_ROTATION_RANGE) * -1;
        const rY = mouseX / width - HALF_ROTATION_RANGE;
        x.set(rX);
        y.set(rY);
    };
    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };
    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
                transformStyle: "preserve-3d",
                transform,
            }}
            className="relative rounded-xl bg-gradient-to-br"
        >
            {children}
            <div
                style={{
                    transform: "translateZ(75px)",
                    transformStyle: "preserve-3d",
                }}
                className="absolute inset-4 grid place-content-center rounded-xl bg-white shadow-lg"
            >
                <p
                    style={{
                        transform: "translateZ(50px)",
                    }}
                    className="text-center text-2xl font-bold"
                >
                    HOVER ME
                </p>
            </div>
        </motion.div>
    );
};

export const StickyBackgound: React.FC<CustomDivProps> = ({ className, children, ...props }) => {
    const targetRef = useRef(null)
    const { scrollYProgress } = useScroll({
        target: targetRef,
        offset: ["end end", "end start"],
    });
    const scale = useTransform(scrollYProgress, [0.5, 1], [1, 0.8]);
    const opacity = useTransform(scrollYProgress, [0.3, 1], [1, 0.7]);
    return (
        <motion.div {...props} className={className} ref={targetRef} style={{
            backgroundSize: "cover",
            scale,
            top: IMG_PADDING,
            opacity
        }}>
            {children}
        </motion.div>
    )
}
export const OverlayText = ({
    children
}: {
    children: ReactNode;
}) => {
    const targetRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: targetRef,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [1, -1]);
    const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75], [0, 1, 0]);
    return (
        <motion.div
            transition={{ type: "spring", }}
            style={{
                y,
                opacity,
            }}
            ref={targetRef}
        >
            {children}
        </motion.div>
    );
};

export const TextParallax: React.FC<CustomAnchorProps> = React.memo(
    ({ className, children, ...props }) => {
        const targetRef = useRef<HTMLDivElement>(null)
        const { scrollYProgress } = useScroll({
            target: targetRef,
            offset: ["start end", "end start"],
        })
        const y = useTransform(scrollYProgress, [0, 1], [1, -1])
        const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75], [0.7, 1, 0.7]);
        const scale = useTransform(scrollYProgress, [0.1, 0.5, 0.1], [1, 0.3, 1]);
        return (
            <motion.div style={{ y, opacity, scale }} ref={targetRef}>
                {children}
            </motion.div>
        )
    }
)

export const DisplayFromBellow: React.FC<CustomDivProps> = React.memo(
    ({ className, children, ...props }) => {
        const targetRef = useRef<HTMLDivElement>(null)
        const { scrollYProgress } = useScroll({
            target: targetRef,
            offset: ["start end", "center end"],
        });
        const y = useTransform(scrollYProgress, [1, 0], [0, 150], { ease: circOut })
        const opacity = useTransform(scrollYProgress, [1, 0], [1, 0.3])
        const { isMedium } = useMediaQuery()
        return <motion.div className={className} ref={targetRef} style={isMedium ? {} : { y, opacity }}>
            {children}
        </motion.div>
    }
)

DisplayFromBellow.displayName = "DisplayFromBellow"
export const ZoomOnView: React.FC<CustomAnchorProps> = React.memo(({ className, children, ...props }) => {
    const targetRef = useRef<HTMLDivElement>(null)
    // const { scrollYProgress } = useScroll({
    //     target: targetRef,
    //     offset: ["start start", "end end"],
    // });
    return <motion.div className={twMerge(className)} ref={targetRef}>
        {children}
    </motion.div>
})

// export const StickyImage = React.memo(({ imgUrl }: { imgUrl?: string }) => {
//     const targetRef = useRef(null);
//     const { scrollYProgress } = useScroll({
//         target: targetRef,
//         offset: ["end end", "end start"],
//     });
//     const scale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
//     const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
//     return (
//         <motion.div
//             style={{
//                 backgroundImage: `url(${imgUrl})`,
//                 backgroundSize: "cover",
//                 backgroundPosition: "center",
//                 height: `calc(100vh - ${IMG_PADDING * 2}px)`,
//                 top: IMG_PADDING,
//                 scale,
//             }}
//             ref={targetRef}
//             className="sticky z-0 overflow-hidden rounded-3xl"
//         >
//             <motion.div
//                 className="absolute inset-0 bg-neutral-950/70"
//                 style={{
//                     opacity,
//                 }}
//             />
//         </motion.div>
//     );
// }
// )
export const StaggeringTextAnimation: React.FC<StaggeringTextAnimationProps> = React.memo(({ children, className, inverse = false, mirrorElement, asChild = false, ...props }) => {
    const Element = asChild ? motion.span : motion.a;
    return <Element {...props} whileHover={"hoveredAnimation"} initial="initial" className={twMerge("relative overflow-hidden w-fit whitespace-nowrap inline-block", className)}>
        <motion.span transition={{ type: "spring", mass: 0.4, stiffness: 90 }} variants={{
            initial: { y: 0 },
            hoveredAnimation: { y: inverse ? "200%" : "-200%" }
        }} >
            <span>
                {children}
            </span>
        </motion.span>
        <motion.span transition={{ type: "spring", mass: 0.4, stiffness: 90 }} variants={{
            initial: { y: inverse ? "-200%" : "200%", opacity: 0 },
            hoveredAnimation: { y: 0, opacity: 1 }
        }} className="absolute">
            <span>
                {mirrorElement || children}
            </span>
        </motion.span>
    </Element>
})

export const StaggeringDivAnimation: React.FC<CustomDivProps> = React.memo(
    ({ className, children, ...props }) => {
        return <motion.div {...props} whileHover={"hoveredAnimation"} initial="initial" className={twMerge("relative overflow-hidden w-fit whitespace-nowrap inline-block", className)}>
            <motion.span transition={{ type: "spring", mass: 0.4, stiffness: 90 }} variants={{
                initial: { y: 0 },
                hoveredAnimation: { y: "-200%" }
            }} >
                <span>
                    {children}
                </span>
            </motion.span>
            <motion.span transition={{ type: "spring", mass: 0.4, stiffness: 90 }} variants={{
                initial: { y: "200%", opacity: 0 },
                hoveredAnimation: { y: 0, opacity: 1 }
            }} className="absolute">
                <span>
                    {children}
                </span>
            </motion.span>
        </motion.div>
    }
)